/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useLocalizedPrice } from '../utils/priceUtils';
import { DiscountService } from '../services/discountService';
import { useTranslation } from '../i18n/useTranslation';
import { motion, AnimatePresence } from 'framer-motion';

interface PriceDisplayProps {
    basePriceEUR: number;
    serviceId?: string;
    tierId?: string;
    fallbackKey?: string;
    className?: string;
    showCampaignBadge?: boolean;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
    basePriceEUR,
    serviceId,
    tierId,
    fallbackKey,
    className = "",
    showCampaignBadge = true
}) => {
    const { convertAndFormat, currency } = useLocalizedPrice();
    const { t } = useTranslation();
    const [prices, setPrices] = React.useState<{
        original: string | null;
        discounted: string | null;
    }>({ original: null, discounted: null });

    const discountInfo = React.useMemo(() => {
        return DiscountService.calculateDiscount(basePriceEUR, serviceId, tierId);
    }, [basePriceEUR, serviceId, tierId]);

    React.useEffect(() => {
        let isMounted = true;

        const updatePrices = async () => {
            const original = await convertAndFormat(basePriceEUR);
            const discounted = discountInfo.appliedCampaign
                ? await convertAndFormat(discountInfo.discountedPrice)
                : original;

            if (isMounted) {
                setPrices({ original, discounted });
            }
        };

        updatePrices();
        return () => { isMounted = false; };
    }, [basePriceEUR, discountInfo, convertAndFormat]);

    if (!prices.original) {
        return <span className="animate-pulse opacity-50">{fallbackKey ? t(fallbackKey) : '...'}</span>;
    }

    const hasDiscount = !!discountInfo.appliedCampaign;

    return (
        <div className={`flex flex-col ${className}`}>
            <div className="flex items-center gap-2">
                {hasDiscount && (
                    <span className="text-sm line-through text-gray-400 dark:text-gray-500 font-medium">
                        {prices.original}
                    </span>
                )}
                <motion.span
                    key={prices.discounted}
                    initial={hasDiscount ? { scale: 1.1, color: '#f59e0b' } : {}}
                    animate={{ scale: 1, color: 'inherit' }}
                    className={`font-bold ${hasDiscount ? 'text-amber-500 dark:text-amber-400' : ''}`}
                >
                    {prices.discounted}
                </motion.span>
            </div>

            <AnimatePresence>
                {hasDiscount && showCampaignBadge && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-1 mt-1"
                    >
                        <span className="text-[10px] font-black bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 rounded-md uppercase tracking-tighter">
                            {t(discountInfo.appliedCampaign?.nameKey || '')}
                        </span>
                        {discountInfo.appliedCampaign?.discountPercentage && (
                            <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400">
                                -{discountInfo.appliedCampaign.discountPercentage}%
                            </span>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PriceDisplay;
